import {
  AnalyticsContext as CarvanaAnalyticsContext,
  ContextType
} from '@carvana/eventable-clickstream';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef
} from 'react';
import { useRequestIds } from '~/context/srp/InventoryContext';

type SearchAnalyticsContextType = ContextType;

const defaultContext: SearchAnalyticsContextType = {
  trackEvent: () => {}
};

const SearchAnalyticsContext =
  createContext<SearchAnalyticsContextType>(defaultContext);

export function SearchAnalyticsContextProvider({ children }) {
  const { trackEvent, ...rest } = useContext(CarvanaAnalyticsContext);
  const requestIds = useRequestIds();
  const requestIdsRef = useRef(requestIds);

  useEffect(() => {
    requestIdsRef.current = requestIds;
  }, [requestIds]);

  const customTrackEvent: ContextType['trackEvent'] = useCallback(
    (eventName, customAttributes, overrides, options) => {
      trackEvent(
        eventName,
        {
          ...customAttributes,
          currentSearchRequestId: requestIdsRef.current.searchRequestId,
          previousSearchRequestId: requestIdsRef.current.previousSearchRequestId
        },
        overrides,
        options
      );
    },
    [trackEvent]
  );

  const value = useMemo<SearchAnalyticsContextType>(
    () => ({
      ...rest,
      trackEvent: customTrackEvent
    }),
    [customTrackEvent, rest]
  );

  return (
    <SearchAnalyticsContext.Provider value={value}>
      {children}
    </SearchAnalyticsContext.Provider>
  );
}

SearchAnalyticsContextProvider.displayName = 'SearchAnalyticsContextProvider';

export function useTrackEvent() {
  return useContext(SearchAnalyticsContext).trackEvent;
}

export default SearchAnalyticsContext;
